import React, { useState, useEffect, useMemo } from 'react';
import { 
  Search, Filter, Calendar, Clock, Users, Dumbbell, Music, 
  Waves, Swords, Activity, ChevronRight, ChevronDown, 
  MapPin, AlertCircle, Info, X, Sparkles, GraduationCap,
  CheckCircle2, ArrowRight, LayoutGrid, List, User
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';

const API_URL = window.location.origin || '';

// ============================================
// ÍCONES POR MODALIDADE
// ============================================
const ModalityIcons = {
  Swords: Swords,
  Activity: Activity,
  Music: Music,
  Waves: Waves,
  Dumbbell: Dumbbell
};

// ============================================
// COMPONENTE: PageHeader Premium
// ============================================
const PageHeader = () => (
  <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-purple-600 via-purple-700 to-indigo-800 p-6 sm:p-8 text-white">
    {/* Padrão de DNA no fundo */}
    <div className="absolute inset-0 opacity-10">
      <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
        <defs>
          <pattern id="dna-pattern" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
            <circle cx="10" cy="10" r="1" fill="currentColor" />
            <path d="M0 10 Q10 5, 20 10 T40 10" fill="none" stroke="currentColor" strokeWidth="0.5" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#dna-pattern)" />
      </svg>
    </div>
    
    <div className="relative z-10">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-yellow-300" />
            </div>
            <div>
              <Badge className="bg-white/20 text-white border-0 backdrop-blur mb-1">
                DNA CONNECT
              </Badge>
              <h1 className="text-2xl sm:text-3xl font-bold">Aulas Extras 2026</h1>
            </div>
          </div>
          <p className="text-purple-100 max-w-xl text-sm sm:text-base">
            Consulte os horários das atividades extracurriculares e encontre a rotina ideal para seu filho
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="text-right hidden sm:block">
            <p className="text-purple-200 text-sm">Ano Letivo</p>
            <p className="text-xl font-bold">2026</p>
          </div>
          <div className="w-16 h-16 bg-white/10 backdrop-blur rounded-2xl flex items-center justify-center">
            <GraduationCap className="w-8 h-8" />
          </div>
        </div>
      </div>
    </div>
  </div>
);

// ============================================
// COMPONENTE: Stats Cards
// ============================================
const StatsCards = ({ stats, onModalityClick }) => {
  if (!stats) return null;
  
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {stats.modalities_summary?.map((mod) => {
        const IconComponent = ModalityIcons[mod.icon] || Activity;
        return (
          <Card 
            key={mod.id}
            className="cursor-pointer hover:shadow-lg transition-all hover:-translate-y-1 border-0 bg-gradient-to-br from-white to-gray-50"
            onClick={() => onModalityClick(mod.id)}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div 
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${mod.color}20` }}
                >
                  <IconComponent className="w-5 h-5" style={{ color: mod.color }} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{mod.total_schedules}</p>
                  <p className="text-xs text-gray-500">{mod.name}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

// ============================================
// COMPONENTE: Announcement Banner
// ============================================
const AnnouncementBanner = ({ announcement }) => {
  const [expanded, setExpanded] = useState(false);
  
  if (!announcement) return null;
  
  return (
    <Card className="border-l-4 border-l-amber-500 bg-gradient-to-r from-amber-50 to-orange-50">
      <CardContent className="p-4 sm:p-6">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <AlertCircle className="w-5 h-5 text-amber-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-amber-900 mb-2">{announcement.title}</h3>
            <div className={`text-amber-800 text-sm whitespace-pre-line ${!expanded && 'line-clamp-3'}`}>
              {announcement.content}
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              className="mt-2 text-amber-700 hover:text-amber-900 p-0 h-auto"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? 'Ver menos' : 'Ver mais'}
              <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${expanded && 'rotate-180'}`} />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ============================================
// COMPONENTE: FilterBar
// ============================================
const FilterBar = ({ 
  classes, 
  modalities, 
  filters, 
  setFilters, 
  viewMode, 
  setViewMode,
  onOpenRoutine 
}) => {
  const weekdays = [
    { value: '', label: 'Todos os dias' },
    { value: 'segunda', label: 'Segunda-feira' },
    { value: 'terca', label: 'Terça-feira' },
    { value: 'quarta', label: 'Quarta-feira' },
    { value: 'quinta', label: 'Quinta-feira' },
    { value: 'sexta', label: 'Sexta-feira' },
  ];
  
  const periods = [
    { value: '', label: 'Todos os períodos' },
    { value: 'manha', label: 'Pedagógico Manhã' },
    { value: 'tarde', label: 'Pedagógico Tarde' },
  ];

  return (
    <Card className="border-0 shadow-lg bg-white/80 backdrop-blur">
      <CardContent className="p-4">
        <div className="flex flex-col gap-4">
          {/* Linha 1: Busca e Turma */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Buscar turma..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="pl-10"
              />
            </div>
            
            <Select 
              value={filters.classId || 'all'} 
              onValueChange={(v) => setFilters({ ...filters, classId: v === 'all' ? '' : v })}
            >
              <SelectTrigger className="w-full sm:w-[200px]">
                <SelectValue placeholder="Selecione a turma" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as turmas</SelectItem>
                {classes.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Linha 2: Filtros adicionais */}
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
            <div className="flex flex-wrap gap-2 flex-1">
              <Select 
                value={filters.modalityId || 'all'} 
                onValueChange={(v) => setFilters({ ...filters, modalityId: v === 'all' ? '' : v })}
              >
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Modalidade" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {modalities.map((m) => (
                    <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select 
                value={filters.weekday || 'all'} 
                onValueChange={(v) => setFilters({ ...filters, weekday: v === 'all' ? '' : v })}
              >
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Dia da semana" />
                </SelectTrigger>
                <SelectContent>
                  {weekdays.map((w) => (
                    <SelectItem key={w.value || 'all'} value={w.value || 'all'}>{w.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select 
                value={filters.period || 'all'} 
                onValueChange={(v) => setFilters({ ...filters, period: v === 'all' ? '' : v })}
              >
                <SelectTrigger className="w-[170px]">
                  <SelectValue placeholder="Período" />
                </SelectTrigger>
                <SelectContent>
                  {periods.map((p) => (
                    <SelectItem key={p.value || 'all'} value={p.value || 'all'}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Toggle de visualização */}
            <div className="flex items-center gap-2">
              <Button
                variant={viewMode === 'week' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('week')}
                className="gap-1"
              >
                <Calendar className="w-4 h-4" />
                <span className="hidden sm:inline">Semanal</span>
              </Button>
              <Button
                variant={viewMode === 'modality' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('modality')}
                className="gap-1"
              >
                <LayoutGrid className="w-4 h-4" />
                <span className="hidden sm:inline">Modalidade</span>
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={onOpenRoutine}
                className="gap-1 bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:from-purple-700 hover:to-indigo-700"
              >
                <User className="w-4 h-4" />
                <span className="hidden sm:inline">Ver Rotina</span>
              </Button>
            </div>
          </div>
          
          {/* Tags de filtros ativos */}
          {(filters.classId || filters.modalityId || filters.weekday || filters.period) && (
            <div className="flex flex-wrap gap-2">
              {filters.classId && (
                <Badge variant="secondary" className="gap-1">
                  {classes.find(c => c.id === filters.classId)?.name}
                  <X 
                    className="w-3 h-3 cursor-pointer" 
                    onClick={() => setFilters({ ...filters, classId: '' })} 
                  />
                </Badge>
              )}
              {filters.modalityId && (
                <Badge variant="secondary" className="gap-1">
                  {modalities.find(m => m.id === filters.modalityId)?.name}
                  <X 
                    className="w-3 h-3 cursor-pointer" 
                    onClick={() => setFilters({ ...filters, modalityId: '' })} 
                  />
                </Badge>
              )}
              {filters.weekday && (
                <Badge variant="secondary" className="gap-1">
                  {filters.weekday.charAt(0).toUpperCase() + filters.weekday.slice(1)}
                  <X 
                    className="w-3 h-3 cursor-pointer" 
                    onClick={() => setFilters({ ...filters, weekday: '' })} 
                  />
                </Badge>
              )}
              {filters.period && (
                <Badge variant="secondary" className="gap-1">
                  Ped. {filters.period === 'manha' ? 'Manhã' : 'Tarde'}
                  <X 
                    className="w-3 h-3 cursor-pointer" 
                    onClick={() => setFilters({ ...filters, period: '' })} 
                  />
                </Badge>
              )}
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setFilters({ search: '', classId: '', modalityId: '', weekday: '', period: '' })}
                className="text-gray-500 h-6"
              >
                Limpar filtros
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// ============================================
// COMPONENTE: ActivityCard
// ============================================
const ActivityCard = ({ schedule, compact = false }) => {
  const IconComponent = ModalityIcons[schedule.modality_icon] || Activity;
  
  return (
    <div 
      className={`rounded-xl p-3 border-l-4 bg-white shadow-sm hover:shadow-md transition-all ${compact ? 'text-sm' : ''}`}
      style={{ borderLeftColor: schedule.modality_color }}
    >
      <div className="flex items-start gap-3">
        <div 
          className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `${schedule.modality_color}15` }}
        >
          <IconComponent className="w-5 h-5" style={{ color: schedule.modality_color }} />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-semibold text-gray-900">{schedule.modality_name}</span>
            {schedule.is_contraturno && (
              <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                Contraturno
              </Badge>
            )}
          </div>
          
          <div className="flex items-center gap-2 text-gray-600 text-sm mb-2">
            <Clock className="w-3.5 h-3.5" />
            <span className="font-medium">{schedule.start_time} - {schedule.end_time}</span>
            {schedule.departure_time && (
              <span className="text-orange-600 font-medium">
                (Saída: {schedule.departure_time})
              </span>
            )}
          </div>
          
          <div className="flex flex-wrap gap-1">
            {schedule.class_names?.slice(0, compact ? 3 : 10).map((className, idx) => (
              <Badge 
                key={idx} 
                variant="secondary" 
                className="text-xs bg-gray-100 text-gray-700"
              >
                {className}
              </Badge>
            ))}
            {schedule.class_names?.length > (compact ? 3 : 10) && (
              <Badge variant="secondary" className="text-xs">
                +{schedule.class_names.length - (compact ? 3 : 10)}
              </Badge>
            )}
          </div>
          
          {schedule.notes && (
            <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
              <Info className="w-3 h-3" />
              {schedule.notes}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================
// COMPONENTE: DayCard (Grade Semanal)
// ============================================
const DayCard = ({ day, schedules }) => {
  const dayNames = {
    segunda: { full: 'Segunda-feira', short: 'Seg' },
    terca: { full: 'Terça-feira', short: 'Ter' },
    quarta: { full: 'Quarta-feira', short: 'Qua' },
    quinta: { full: 'Quinta-feira', short: 'Qui' },
    sexta: { full: 'Sexta-feira', short: 'Sex' },
  };
  
  const dayInfo = dayNames[day] || { full: day, short: day };
  const hasActivities = schedules.length > 0;
  
  return (
    <Card className={`overflow-hidden ${!hasActivities && 'opacity-60'}`}>
      <CardHeader className="py-3 px-4 bg-gradient-to-r from-purple-50 to-indigo-50">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-bold text-purple-900">
            <span className="hidden sm:inline">{dayInfo.full}</span>
            <span className="sm:hidden">{dayInfo.short}</span>
          </CardTitle>
          <Badge variant="secondary" className="bg-white">
            {schedules.length} {schedules.length === 1 ? 'aula' : 'aulas'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="p-3 space-y-2 max-h-[400px] overflow-y-auto">
        {hasActivities ? (
          schedules.map((schedule) => (
            <ActivityCard key={schedule.id} schedule={schedule} compact />
          ))
        ) : (
          <div className="text-center py-6 text-gray-400">
            <Calendar className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">Sem atividades</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ============================================
// COMPONENTE: WeekScheduleGrid
// ============================================
const WeekScheduleGrid = ({ schedules }) => {
  const weekdays = ['segunda', 'terca', 'quarta', 'quinta', 'sexta'];
  
  const schedulesByDay = useMemo(() => {
    const grouped = {};
    weekdays.forEach(day => {
      grouped[day] = schedules
        .filter(s => s.weekday === day)
        .sort((a, b) => a.start_time.localeCompare(b.start_time));
    });
    return grouped;
  }, [schedules]);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
      {weekdays.map(day => (
        <DayCard key={day} day={day} schedules={schedulesByDay[day]} />
      ))}
    </div>
  );
};

// ============================================
// COMPONENTE: ModalityView
// ============================================
const ModalityView = ({ modalities, schedules }) => {
  const [selectedModality, setSelectedModality] = useState(null);
  
  const schedulesByModality = useMemo(() => {
    const grouped = {};
    modalities.forEach(m => {
      grouped[m.id] = schedules.filter(s => s.modality_id === m.id);
    });
    return grouped;
  }, [modalities, schedules]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {modalities.map((mod) => {
          const IconComponent = ModalityIcons[mod.icon] || Activity;
          const count = schedulesByModality[mod.id]?.length || 0;
          const isSelected = selectedModality === mod.id;
          
          return (
            <Card 
              key={mod.id}
              className={`cursor-pointer transition-all hover:-translate-y-1 ${
                isSelected ? 'ring-2 ring-purple-500 shadow-lg' : 'hover:shadow-md'
              }`}
              onClick={() => setSelectedModality(isSelected ? null : mod.id)}
            >
              <CardContent className="p-4">
                <div 
                  className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 bg-gradient-to-br ${mod.gradient}`}
                >
                  <IconComponent className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-bold text-gray-900">{mod.name}</h3>
                <p className="text-sm text-gray-500">{count} horários</p>
              </CardContent>
            </Card>
          );
        })}
      </div>
      
      {selectedModality && (
        <Card className="border-0 shadow-lg">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2">
              {(() => {
                const mod = modalities.find(m => m.id === selectedModality);
                const IconComponent = ModalityIcons[mod?.icon] || Activity;
                return (
                  <>
                    <div 
                      className="w-8 h-8 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: mod?.color }}
                    >
                      <IconComponent className="w-4 h-4 text-white" />
                    </div>
                    Horários de {mod?.name}
                  </>
                );
              })()}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {schedulesByModality[selectedModality]?.map((schedule) => (
              <ActivityCard key={schedule.id} schedule={schedule} />
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ============================================
// COMPONENTE: RoutinePanel (Modal Premium)
// ============================================
const RoutinePanel = ({ open, onClose, classes }) => {
  const [selectedClass, setSelectedClass] = useState('');
  const [routine, setRoutine] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (selectedClass) {
      loadRoutine(selectedClass);
    }
  }, [selectedClass]);

  const loadRoutine = async (classId) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/dnaconnect/aulas-extras/schedules/by-class/${classId}`);
      const data = await res.json();
      setRoutine(data);
    } catch (error) {
      toast.error('Erro ao carregar rotina');
    } finally {
      setLoading(false);
    }
  };

  const weekdayNames = {
    segunda: 'Segunda-feira',
    terca: 'Terça-feira',
    quarta: 'Quarta-feira',
    quinta: 'Quinta-feira',
    sexta: 'Sexta-feira',
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="pb-4 border-b">
          <DialogTitle className="flex items-center gap-2 text-xl">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-xl flex items-center justify-center">
              <User className="w-5 h-5 text-white" />
            </div>
            Minha Rotina de Aulas Extras
          </DialogTitle>
          <DialogDescription>
            Selecione a turma para ver a rotina semanal completa
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <Select value={selectedClass} onValueChange={setSelectedClass}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Selecione a turma do seu filho" />
            </SelectTrigger>
            <SelectContent>
              {classes.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">
                      {c.segment}
                    </Badge>
                    {c.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : routine ? (
            <div className="space-y-4">
              {/* Header da turma */}
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-bold text-purple-900 text-lg">{routine.class_info?.name}</h3>
                    <p className="text-purple-600 text-sm">
                      {routine.class_info?.segment} • Pedagógico {routine.class_info?.pedagogy_period === 'manha' ? 'Manhã' : 'Tarde'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-purple-900">{routine.total_activities}</p>
                    <p className="text-xs text-purple-600">atividades/semana</p>
                  </div>
                </div>
                
                {routine.modalities?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {routine.modalities.map((mod, idx) => (
                      <Badge key={idx} className="bg-white text-purple-700">
                        {mod}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              {/* Timeline semanal */}
              <div className="space-y-3">
                {Object.entries(routine.schedules_by_day || {}).map(([day, daySchedules]) => {
                  if (daySchedules.length === 0) return null;
                  
                  return (
                    <Card key={day} className="overflow-hidden">
                      <CardHeader className="py-2 px-4 bg-gray-50">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-sm font-semibold text-gray-700">
                            {weekdayNames[day]}
                          </CardTitle>
                          <Badge variant="secondary">{daySchedules.length}</Badge>
                        </div>
                      </CardHeader>
                      <CardContent className="p-3 space-y-2">
                        {daySchedules.map((schedule) => (
                          <div 
                            key={schedule.id}
                            className="flex items-center gap-3 p-3 rounded-lg bg-gray-50"
                          >
                            <div 
                              className="w-1 h-12 rounded-full"
                              style={{ backgroundColor: schedule.modality_color }}
                            />
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-semibold">{schedule.modality_name}</span>
                                {schedule.is_contraturno && (
                                  <Badge variant="outline" className="text-xs bg-blue-50 text-blue-600">
                                    Contraturno
                                  </Badge>
                                )}
                              </div>
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <Clock className="w-3.5 h-3.5" />
                                {schedule.start_time} - {schedule.end_time}
                                {schedule.departure_time && (
                                  <span className="text-orange-600">
                                    • Saída {schedule.departure_time}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
              
              {routine.total_activities === 0 && (
                <div className="text-center py-12 text-gray-400">
                  <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Esta turma não possui aulas extras cadastradas</p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-400">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Selecione uma turma para ver a rotina</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ============================================
// COMPONENTE PRINCIPAL: AulasExtras
// ============================================
export default function AulasExtras() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [classes, setClasses] = useState([]);
  const [modalities, setModalities] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    classId: '',
    modalityId: '',
    weekday: '',
    period: ''
  });
  const [viewMode, setViewMode] = useState('week');
  const [routineOpen, setRoutineOpen] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, classesRes, modalitiesRes, schedulesRes, announcementsRes] = await Promise.all([
        fetch(`${API_URL}/api/dnaconnect/aulas-extras/stats`),
        fetch(`${API_URL}/api/dnaconnect/aulas-extras/classes`),
        fetch(`${API_URL}/api/dnaconnect/aulas-extras/modalities`),
        fetch(`${API_URL}/api/dnaconnect/aulas-extras/schedules`),
        fetch(`${API_URL}/api/dnaconnect/aulas-extras/announcements`)
      ]);

      setStats(await statsRes.json());
      setClasses(await classesRes.json());
      setModalities(await modalitiesRes.json());
      setSchedules(await schedulesRes.json());
      setAnnouncements(await announcementsRes.json());
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  // Filtrar horários
  const filteredSchedules = useMemo(() => {
    let result = schedules;
    
    if (filters.search) {
      const search = filters.search.toLowerCase();
      result = result.filter(s => 
        s.class_names?.some(cn => cn.toLowerCase().includes(search)) ||
        s.modality_name?.toLowerCase().includes(search)
      );
    }
    
    if (filters.classId) {
      result = result.filter(s => s.classes?.includes(filters.classId));
    }
    
    if (filters.modalityId) {
      result = result.filter(s => s.modality_id === filters.modalityId);
    }
    
    if (filters.weekday) {
      result = result.filter(s => s.weekday === filters.weekday);
    }
    
    if (filters.period) {
      result = result.filter(s => s.pedagogy_period === filters.period);
    }
    
    return result;
  }, [schedules, filters]);

  // Filtrar classes pela busca
  const filteredClasses = useMemo(() => {
    if (!filters.search) return classes;
    const search = filters.search.toLowerCase();
    return classes.filter(c => c.name.toLowerCase().includes(search));
  }, [classes, filters.search]);

  const handleModalityClick = (modalityId) => {
    setFilters({ ...filters, modalityId });
    setViewMode('modality');
  };

  if (loading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-500">Carregando aulas extras...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-8">
      {/* Header Premium */}
      <PageHeader />
      
      {/* Stats Cards */}
      <StatsCards stats={stats} onModalityClick={handleModalityClick} />
      
      {/* Aviso Institucional */}
      {announcements[0] && <AnnouncementBanner announcement={announcements[0]} />}
      
      {/* Barra de Filtros */}
      <FilterBar 
        classes={filteredClasses}
        modalities={modalities}
        filters={filters}
        setFilters={setFilters}
        viewMode={viewMode}
        setViewMode={setViewMode}
        onOpenRoutine={() => setRoutineOpen(true)}
      />
      
      {/* Conteúdo Principal */}
      {viewMode === 'week' ? (
        <WeekScheduleGrid schedules={filteredSchedules} />
      ) : (
        <ModalityView modalities={modalities} schedules={filteredSchedules} />
      )}
      
      {/* Empty State */}
      {filteredSchedules.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <Search className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">Nenhum resultado encontrado</h3>
            <p className="text-gray-500 mb-4">Tente ajustar os filtros para ver mais resultados</p>
            <Button 
              variant="outline" 
              onClick={() => setFilters({ search: '', classId: '', modalityId: '', weekday: '', period: '' })}
            >
              Limpar filtros
            </Button>
          </CardContent>
        </Card>
      )}
      
      {/* Modal de Rotina por Turma */}
      <RoutinePanel 
        open={routineOpen} 
        onClose={() => setRoutineOpen(false)} 
        classes={classes}
      />
    </div>
  );
}
